import { useDisplayCurrency } from '../currency/DisplayCurrencyProvider';
import { CurrencyAffixPicker, useShowCurrencySelector } from '../currency/CurrencySelector';
import { decimalSeparatorFor, SYMBOLS } from '@charitystack/shared/currency';
import { readCustomAmount, usdOf } from './formatUtils';
import React, { useEffect, useRef, useState } from 'react';
import { fieldClass } from '@charitystack/shared';
import { formatWithCommas, removeCommas } from './formatUtils';

// A converted amount (a canonical "1234.5") in the donor's separators. A decimal-comma locale
// gets no grouping dot: "2.500" would read as 2.5 after a refocus edit (convert.js reads a dot
// there as the decimal unless 3 digits follow it).
const showConverted = (canonical, decimalComma) => {
  const text = String(canonical);
  return decimalComma ? text.replace('.', ',') : formatWithCommas(text);
};

export default function CustomAmountInput({
  // Standard Form Mode props
  amount = 20,
  setAmount,
  isButtonSelection = false, // true when a preset button is selected (StandardForm only)
  onCustomAmountTyped, // callback to signal user is typing a custom amount (StandardForm only)
  // Crowdfunding Form Mode props
  selectedCrowdfundingOption,
  setSelectedCrowdfundingOption,
  crowdfundingOptions = [],
  // Shared props
  maxTransactionAmount,
  minTransactionAmount = 1.0, // Default minimum $1.00
  elementID,
  brandColors,
  hasError,
  onAmountChange,
}) {
  const ctx = useDisplayCurrency();
  // P3 UI-1: when donors may choose, the code on the field's right edge is the currency picker.
  const showPicker = useShowCurrencySelector();
  const [hint, setHint] = useState('');
  // Owner ruling ui-a: the donor's symbol alone, no "≈". A currency with no symbol of its own
  // shows nothing on the left: its code is already on the right, as the picker or the plain
  // suffix, and saying it twice read as a stutter (owner review 2026-10-28).
  const currencyCode = ctx.converted ? ctx.displayCurrency : 'USD';
  const prefix = ctx.converted ? SYMBOLS[ctx.displayCurrency] || '' : '$';
  const currencyInPlay = ctx.converted || showPicker;
  const currencyDescriptionID = `${elementID}-custom-amount-currency`;
  // cs-allow-hardcoded: prefix clearance follows the currency glyph count. It is the prefix's
  // inset (the same --cs-input-padding-x dial the prefix sits at, donor redesign) plus a gap
  // (rem) and its glyphs (em, the field's own font), so a code prefix such as KWD clears the
  // typed amount (C-A321) at any font-size dial. Plain $/USD without the picker keeps pl-8.
  // With no prefix the amount starts at the field's own inset, like every other field.
  const clearanceStart = !prefix
    ? 'var(--cs-input-padding-x)'
    : ctx.converted || showPicker
      ? `calc(var(--cs-input-padding-x) + 0.625rem + ${prefix.length * 0.75}em)`
      : '2rem';
  // cs-allow-hardcoded: suffix clearance for the plain code, or the wider picker with its chevron.
  // The picker's code is set at the field's font-size dial, so its share is in em (the field's
  // own font, the same dial); its offset, insets and chevron lane are rem, plus the gap. The lane fits the 20 select chevron. At the
  // default 16px this is 5rem; a larger dial widens it with the picker.
  // The plain code keeps the field recipe's suffix lane (pr-14, the donor redesign).
  // The picker's right padding (CurrencySelector: --cs-input-padding-x + --cs-icon-lg) is part of
  // its width, so it is read from the same tokens here and the two cannot drift apart.
  const clearanceEnd = showPicker
    ? 'calc(1rem + var(--cs-input-padding-x) + var(--cs-icon-lg) + 2.25em)'
    : '3.5rem';
  const [displayValue, setDisplayValue] = useState('');
  const lastTypedValueRef = useRef(null);
  const isFocusedRef = useRef(false);
  // Every CON-315 path below runs only while a non-USD currency is shown. A form showing USD,
  // with conversion off or on, reads keys exactly as before (SPEC.md:441).
  const converted = ctx.converted;
  const decimalComma =
    converted && decimalSeparatorFor(typeof navigator === 'undefined' ? undefined : navigator.language) === ',';

  // Determine if we're in crowdfunding mode based on props
  const isCrowdfundingMode = !!selectedCrowdfundingOption || !!setSelectedCrowdfundingOption;

  // Sync display value — mutually exclusive with preset buttons.
  // Only show a value in the input when:
  //   StandardForm: the current amount is NOT from a button click (isButtonSelection === false)
  //   CrowdfundingForm: the selected option has an empty title (custom entry or default, not a card)
  useEffect(() => {
    if (isCrowdfundingMode) {
      const value = selectedCrowdfundingOption?.amount || '';
      const isCustomEntry = !selectedCrowdfundingOption?.title; // empty title = custom/default
      if (!converted) {
        setDisplayValue(value && isCustomEntry ? formatWithCommas(value.toString()) : '');
        return;
      }
      // The donor's own keystroke: keep the text exactly as typed while the field has focus.
      if (isFocusedRef.current && isCustomEntry && lastTypedValueRef.current === String(value)) return;
      lastTypedValueRef.current = null;
      setDisplayValue(value && isCustomEntry ? showConverted(value, decimalComma) : '');
    } else {
      const value = amount || '';
      if (!converted) {
        setDisplayValue(value && !isButtonSelection ? formatWithCommas(value.toString()) : '');
        return;
      }
      if (isFocusedRef.current && !isButtonSelection && lastTypedValueRef.current === String(value)) return;
      lastTypedValueRef.current = null;
      setDisplayValue(value && !isButtonSelection ? showConverted(value, decimalComma) : '');
    }
  }, [
    amount,
    selectedCrowdfundingOption,
    isCrowdfundingMode,
    isButtonSelection,
    converted,
    decimalComma,
  ]);

  const handleOnChange = (event) => {
    // Signal to parent that user is typing a custom amount (clears isButtonSelection)
    if (onCustomAmountTyped) {
      onCustomAmountTyped();
    }

    let value = event.target.value;
    let valueForDisplay = value;

    if (converted) {
      // A refused key keeps the field's own amount, never a selected preset or level (C-A71).
      const previous = isCrowdfundingMode
        ? (selectedCrowdfundingOption?.title ? '' : selectedCrowdfundingOption?.amount || '')
        : (isButtonSelection ? '' : amount);
      const parsed = readCustomAmount(value, ctx, previous);
      setHint(parsed.hint || '');
      value = parsed.value;
      // While focused the donor's text stays as typed ("1.000" is on its way to "1.000,50");
      // blur shows the amount it was read as.
      valueForDisplay = isFocusedRef.current ? event.target.value : showConverted(value, decimalComma);
    } else {
    // Remove commas for processing
    value = removeCommas(value);

    // Only allow numbers and periods
    value = value.replace(/[^0-9.]/g, '');

    // Allow only 1 period
    let decimalCount = (value.match(/\./g) || []).length;
    if (decimalCount == 2) {
      let index = value.lastIndexOf('.');
      value = value.substr(0, index);
    }

    // Limit to two decimal places
    value = value.replace(/(\.\d{2})\d{1,}/g, '$1');

    if (value === '.') {
      value = '0.';
    }

    valueForDisplay = formatWithCommas(value);
    }

    // Prevent typing amounts over the maximum allowed by processor
    // The field keeps its last accepted text, so it never shows an amount that is not charged (C-A75).
    const numericValue = converted ? usdOf(value, ctx) : parseFloat(value) || 0;
    if (numericValue > maxTransactionAmount) {
      return; // Don't update if over max
    }

    // Update display value with commas
    setDisplayValue(valueForDisplay);
    if (converted) lastTypedValueRef.current = String(value);

    // Handle different modes
    if (isCrowdfundingMode) {
      // Always set as custom entry (empty title) when user is typing in the input.
      // This ensures the input keeps displaying the typed value (the display logic
      // uses empty title to distinguish custom entries from card selections).
      // The empty title also keeps every giving level card unhighlighted, even
      // when the typed amount equals a card's price.
      setSelectedCrowdfundingOption({
        amount: value,
        title: '',
        description: '',
      });
    } else {
      // Standard mode: directly set amount
      setAmount(value);
    }

    // Clear error if amount is valid
    if (
      onAmountChange &&
      value !== '' &&
      numericValue >= minTransactionAmount &&
      numericValue <= maxTransactionAmount
    ) {
      onAmountChange();
    }
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  // Blur shows the amount that will be charged, in the donor's separators (C-A73).
  const handleBlur = () => {
    isFocusedRef.current = false;
    if (!converted) return;
    const value = isCrowdfundingMode
      ? (!selectedCrowdfundingOption?.title ? selectedCrowdfundingOption?.amount : '')
      : (!isButtonSelection ? amount : '');
    setDisplayValue(value ? showConverted(value, decimalComma) : '');
  };

  return (
    <div className="w-full">
      <div className="w-full">
        <div className={isCrowdfundingMode ? '' : 'mt-1'}>
          <div
            className="flex items-center"
            id={`${elementID}-custom-amount`}
          >
            <div
              className="relative w-full"
              id={`${elementID}-custom-amount-input-container`}
            >
              <div className="relative">
                {prefix && (
                <span
                  className="dynamic-input-text dynamic-input-affix absolute left-[var(--cs-input-padding-x)] top-1/2 -translate-y-1/2 text-[color:var(--cs-text-secondary)] pointer-events-none"
                  style={{
                    // CON-6: same iOS-zoom guard as the field it sits on, so the
                    // adornment cannot drift away from the input's text size.
                    // CON-8: `dynamic-input-affix` is what makes that true at the
                    // two narrow container tiers. There the ramp raises the FIELD
                    // to the floor with `!important`, which out-ranks this inline
                    // style — so without the marker class the adornment stayed at
                    // the tier size and drifted, on the widest-used phone tier.
                    // The fallback is for the same reason forms.css carries one:
                    // this dial is undefined outside the donor scope.
                    fontSize: 'var(--cs-input-font-size, var(--cs-text-base))',
                  }}
                >
                  {prefix}
                </span>
                )}
                {/* The field's currency for a screen reader ("Other amount, AED"): the visible
                    prefix and suffix are decoration to it. Only where currency is in play, so a
                    USD form with conversion off keeps its markup byte for byte (CON-315). */}
                {currencyInPlay && (
                  <span id={currencyDescriptionID} className="sr-only">
                    {currencyCode}
                  </span>
                )}
                {/* The shadow utility's type hint is load-bearing, not decoration. Without
                    it, Tailwind v3 reads an arbitrary shadow as a shadow COLOUR and the
                    element renders no shadow at all — which is what the dashboard's form
                    previewer did, since it is on v3 and mounts this component. v4 emits an
                    identical rule either way, so the hint costs the donor apps nothing.
                    CON-42. */}
                <input
                  type="text"
                  inputMode="decimal"
                  name="custom-amount"
                  id={`${elementID}-custom-amount-input`}
                  aria-describedby={currencyInPlay ? currencyDescriptionID : undefined}
                  // The hosted page's phone drawer floors the start padding for a prefix; with
                  // none it reads the published clearance as is (hosted-page base.css).
                  data-amount-prefix={prefix ? undefined : 'none'}
                  /* Donor redesign (CON-515, SPEC §C Inputs; Paper 2JU-0): the
                     shared field recipe — 48 tall, radius 12, white, 1px edge
                     (strong on hover), accent border + 1px inset on focus, the
                     error edge when invalid; 16/24 text, tertiary placeholder.
                     The paddings are clearance for the absolutely positioned
                     `$` prefix and `USD` suffix, not the field's base inset. */
                  className={`dynamic-input-text peer h-[var(--cs-input-height)] pl-8 pr-14 ${fieldClass(hasError)}`}
                  style={
                    // P3 UI-1/UI-2: a converted prefix or the in-field picker sets its own
                    // clearance; the plain code keeps the recipe's pr-14. The hosted page's mobile
                    // drawer floors this field's paddings with !important (hosted-page base.css);
                    // it reads the two custom properties so a converted prefix or the picker still
                    // gets its clearance. Plain $/USD sets neither, so that markup keeps the
                    // recipe's pl-8/pr-14.
                    ctx.converted || showPicker
                      ? {
                          paddingLeft: clearanceStart,
                          ...(showPicker ? { paddingRight: clearanceEnd } : {}),
                          '--amount-input-clearance-start': clearanceStart,
                          '--amount-input-clearance-end': clearanceEnd,
                        }
                      : undefined
                  }
                  placeholder="Other amount"
                  value={displayValue}
                  onChange={(e) => handleOnChange(e)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                {showPicker ? (
                  <CurrencyAffixPicker elementID={elementID} />
                ) : (
                  <span
                    className="dynamic-input-text dynamic-input-affix absolute right-[var(--cs-input-padding-x)] top-1/2 -translate-y-1/2 font-sans text-[color:var(--cs-text-secondary)]"
                    style={{
                      // CON-6/CON-8: see the `$` adornment above.
                      fontSize: 'var(--cs-input-font-size, var(--cs-text-base))',
                    }}
                  >
                    {currencyCode}
                  </span>
                )}
              </div>
              {converted && hint && <p className="text-[color:var(--cs-text-muted)]">{hint}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
